import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

const AUTO_HIDE_DURATION = 5000;

/**
 * A toast notification that confirms which product was just added to the cart.
 * Listens for cart add events from product forms; quantity updates share the
 * same event name, so events are filtered by their source.
 */
class CartToastComponent extends Component {
  requiredRefs = ['card', 'image', 'productTitle', 'variantTitle'];

  /** @type {number | null} */
  #hideTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * @param {CustomEvent<{ resource?: { items?: Array<Object> }, sourceId?: string, data?: { didError?: boolean, source?: string } }>} event
   */
  #handleCartAdd = (event) => {
    const { resource, sourceId, data } = event.detail ?? {};

    if (data?.didError || data?.source !== 'product-form-component') return;

    // The drawer itself is enough feedback when it auto-opens or is already open
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer?.hasAttribute('auto-open')) return;
    if (drawer?.querySelector('dialog[open]')) return;

    const items = Array.isArray(resource?.items) ? resource.items : [];
    const variantId = Number(sourceId);
    // /cart.js lists the most recently added line item first
    const item = items.find((lineItem) => lineItem.id === variantId) ?? items[0];

    if (!item) return;

    this.#render(item);
    this.#show();
  };

  /**
   * @param {{ image?: string, product_title?: string, title?: string, variant_title?: string | null }} item
   */
  #render(item) {
    const { image, productTitle, variantTitle } = this.refs;

    if (image instanceof HTMLImageElement) {
      if (item.image) {
        const separator = item.image.includes('?') ? '&' : '?';
        image.src = `${item.image}${separator}width=160`;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
      }
    }

    if (productTitle) {
      productTitle.textContent = item.product_title ?? item.title ?? '';
    }

    if (variantTitle) {
      const variant = item.variant_title ?? '';
      variantTitle.textContent = variant;
      variantTitle.hidden = variant === '';
    }
  }

  #show() {
    if (this.#hideTimeout) clearTimeout(this.#hideTimeout);

    this.removeAttribute('hidden');
    // Restart the slide-in animation when the toast is already visible
    this.classList.remove('cart-toast--visible');
    // eslint-disable-next-line no-unused-expressions
    this.offsetHeight;
    this.classList.add('cart-toast--visible');

    this.#hideTimeout = setTimeout(() => this.hide(), AUTO_HIDE_DURATION);
  }

  hide = () => {
    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout);
      this.#hideTimeout = null;
    }

    this.classList.remove('cart-toast--visible');
    this.setAttribute('hidden', '');
  };

  openCart = () => {
    this.hide();

    const drawer = document.querySelector('cart-drawer-component');

    if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function') {
      /** @type {any} */ (drawer).open();
    } else {
      window.location.href = Theme.routes.cart_url || '/cart';
    }
  };
}

if (!customElements.get('cart-toast-component')) {
  customElements.define('cart-toast-component', CartToastComponent);
}
